import axios from 'axios';
import type { InternalAxiosRequestConfig } from 'axios';
import { getEnvironmentId } from '@utils/environmentId';
import { getCsrfToken, buildLoginUrl } from '../../contexts/AuthContext';
import { getIsOnline } from '../../services/connectivityService';
import { reportApiError } from '../../services/errorReportingService';

// When VITE_VOLTBOT_API_BASE_URL is '' (deployed), use '/' so the OpenAPI generated
// client sees a truthy baseURL and doesn't fall back to its hardcoded http://localhost
const envBaseUrl = import.meta.env.VITE_VOLTBOT_API_BASE_URL;
const bffBaseUrl =
  envBaseUrl != null ? envBaseUrl || '/' : 'https://localhost:5002';

export const bffApiClient = axios.create({
  baseURL: bffBaseUrl,
  withCredentials: true, // Required for cross-origin cookie sending
  headers: {
    'Content-Type': 'application/json',
    accept: 'text/plain'
  }
});

bffApiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const environmentId = getEnvironmentId();
    if (environmentId) {
      config.headers['X-Environment-Id'] = environmentId;
    }

    // Add CSRF token for mutating requests
    if (['post', 'put', 'delete', 'patch'].includes(config.method ?? '')) {
      const token = getCsrfToken();
      if (token) {
        config.headers['X-CSRF-TOKEN'] = token;
      }
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// 401 handler with redirect debounce
let isRedirecting = false;

bffApiClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401 && !isRedirecting && getIsOnline()) {
      isRedirecting = true;
      setTimeout(() => {
        isRedirecting = false;
      }, 5000);
      window.location.href = buildLoginUrl(window.location.href);
    }
    // Skip reporting errors from the error-inbox endpoint itself to avoid loops
    if (!error.config?.url?.includes('/api/error-inbox')) {
      reportApiError(error);
    }
    return Promise.reject(error);
  }
);
